import { useState } from "react";
import {
    Box,
    Button,
    TextField,
    useMediaQuery,
    Typography,
    useTheme,
    Avatar,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLogin } from "state";
import Dropzone from "react-dropzone";
import { BASE_URL } from "../../config";
import uploadToCloudinary from "../../uploadToCloudinary";
import Navbar from "scenes/navbar";

const profileSchema = yup.object().shape({
    firstName: yup.string().required("required"),
    lastName: yup.string().required("required"),
    email: yup.string().email("invalid email").required("required"),
    currentPassword: yup.string().required("Current password is required"),
    newPassword: yup.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: yup.string().oneOf([yup.ref("newPassword"), null], "Passwords must match"),
    picture: yup.string(),
});

const EditProfilePage = () => {
    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [imagePreview, setImagePreview] = useState(null);
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isNonMobile = useMediaQuery("(min-width:600px)");
    const user = useSelector((state) => state.user);
    const token = useSelector((state) => state.token);

    const userInitial = user?.firstName?.charAt(0).toUpperCase() || "";

    const initialValues = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        picture: "",
    };

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            setIsUploading(true);
            setErrorMessage("");

            let picturePath = user.picturePath;
            if (values.picture) {
                picturePath = await uploadToCloudinary(values.picture);
            }

            const response = await fetch(
                `${BASE_URL}/users/${user._id}/update`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        currentPassword: values.currentPassword,
                        newPassword: values.newPassword || undefined,
                        picturePath: picturePath,
                    }),
                }
            );

            const data = await response.json();
            setIsUploading(false);

            if (!response.ok) {
                setErrorMessage(data.error || data.msg || "Update failed. Please try again.");
                return;
            }

            dispatch(setLogin({ user: data, token }));
            navigate("/");
        } catch (error) {
            setIsUploading(false);
            setErrorMessage(error.message || "Failed to update profile. Please try again.");
            console.error("Update error:", error);
        }
    };

    return (
        <Box>
            <Navbar />
            <Box
                width={isNonMobile ? "500px" : "90%"}
                p={isNonMobile ? "2.5rem" : "1.5rem"}
                m="1rem auto"
                borderRadius="12px"
                backgroundColor={palette.background.alt}
                boxShadow="0 2px 10px rgba(0,0,0,0.1)"
            >
                <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={initialValues}
                    validationSchema={profileSchema}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        setFieldValue,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            {/* PROFILE IMAGE */}
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap="0.75rem"
                                mb="2rem"
                            >
                                <Avatar
                                    src={imagePreview || user.picturePath}
                                    alt={`${user.firstName} ${user.lastName}`}
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        bgcolor: palette.primary.main,
                                        fontSize: "2.5rem",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    }}
                                >
                                    {!imagePreview && !user.picturePath && userInitial}
                                </Avatar>
                                <Dropzone
                                    acceptedFiles=".jpg,.jpeg,.png"
                                    multiple={false}
                                    onDrop={(acceptedFiles) => {
                                        const file = acceptedFiles[0];
                                        setFieldValue("picture", file);
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setImagePreview(reader.result);
                                        };
                                        reader.readAsDataURL(file);
                                    }}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <Button
                                            {...getRootProps()}
                                            size="small"
                                            startIcon={<EditOutlinedIcon />}
                                            sx={{
                                                color: palette.primary.main,
                                                textTransform: "none",
                                                fontSize: "0.875rem",
                                                fontWeight: 500,
                                                "&:hover": {
                                                    backgroundColor: "rgba(0, 191, 255, 0.08)",
                                                },
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            Change photo
                                        </Button>
                                    )}
                                </Dropzone>
                            </Box>

                            {/* FORM FIELDS */}
                            <Box display="flex" flexDirection="column" gap="1.25rem">
                                <Box display="grid" gridTemplateColumns="1fr 1fr" gap="1rem">
                                    <TextField
                                        label="First Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.firstName}
                                        name="firstName"
                                        error={Boolean(touched.firstName) && Boolean(errors.firstName)}
                                        helperText={touched.firstName && errors.firstName}
                                        size="small"
                                        fullWidth
                                    />
                                    <TextField
                                        label="Last Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.lastName}
                                        name="lastName"
                                        error={Boolean(touched.lastName) && Boolean(errors.lastName)}
                                        helperText={touched.lastName && errors.lastName}
                                        size="small"
                                        fullWidth
                                    />
                                </Box>

                                <TextField
                                    label="Email"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.email}
                                    name="email"
                                    error={Boolean(touched.email) && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                    size="small"
                                    fullWidth
                                />

                                {/* PASSWORD SECTION */}
                                <Box mt="0.5rem">
                                    <Typography
                                        variant="subtitle2"
                                        color={palette.neutral.medium}
                                        sx={{ mb: "1rem", fontWeight: 500 }}
                                    >
                                        Change Password
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gap="1.25rem">
                                        <TextField
                                            label="Current Password"
                                            type="password"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.currentPassword}
                                            name="currentPassword"
                                            error={Boolean(touched.currentPassword) && Boolean(errors.currentPassword)}
                                            helperText={touched.currentPassword && errors.currentPassword}
                                            size="small"
                                            fullWidth
                                        />
                                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap="1rem">
                                            <TextField
                                                label="New Password"
                                                type="password"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.newPassword}
                                                name="newPassword"
                                                error={Boolean(touched.newPassword) && Boolean(errors.newPassword)}
                                                helperText={touched.newPassword && errors.newPassword}
                                                size="small"
                                                fullWidth
                                            />
                                            <TextField
                                                label="Confirm Password"
                                                type="password"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.confirmPassword}
                                                name="confirmPassword"
                                                error={Boolean(touched.confirmPassword) && Boolean(errors.confirmPassword)}
                                                helperText={touched.confirmPassword && errors.confirmPassword}
                                                size="small"
                                                fullWidth
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* ERROR MESSAGE */}
                            {errorMessage && (
                                <Typography
                                    color="error"
                                    sx={{
                                        mt: "1.25rem",
                                        p: "0.75rem",
                                        backgroundColor: "rgba(211, 47, 47, 0.08)",
                                        borderRadius: "8px",
                                        fontSize: "0.875rem",
                                        border: `1px solid rgba(211, 47, 47, 0.2)`,
                                    }}
                                >
                                    {errorMessage}
                                </Typography>
                            )}

                            {/* BUTTONS */}
                            <Box display="flex" gap="0.75rem" mt="2rem">
                                <Button
                                    fullWidth
                                    type="submit"
                                    disabled={isUploading}
                                    sx={{
                                        py: "0.75rem",
                                        backgroundColor: palette.primary.main,
                                        color: palette.background.alt,
                                        textTransform: "none",
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        boxShadow: "0 2px 8px rgba(0, 191, 255, 0.3)",
                                        "&:hover": {
                                            backgroundColor: palette.primary.light,
                                            boxShadow: "0 4px 12px rgba(0, 191, 255, 0.4)",
                                        },
                                        "&:disabled": {
                                            backgroundColor: palette.neutral.light,
                                            color: palette.neutral.main,
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    {isUploading ? "Saving..." : "Save Changes"}
                                </Button>
                                <Button
                                    fullWidth
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        py: "0.75rem",
                                        backgroundColor: "transparent",
                                        color: palette.neutral.main,
                                        textTransform: "none",
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        border: `1px solid ${palette.neutral.medium}`,
                                        "&:hover": {
                                            backgroundColor: palette.neutral.light,
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default EditProfilePage;
